import React, { useState, useEffect } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { ChangelogModal } from './ChangelogModal';
import { hasNewUpdates, setLastSeenVersion, getCurrentVersion } from '../../data/changelog';

interface UpdatesNotificationProps {
  className?: string;
}

/**
 * Updates notification component with badge indicator
 * Shows a notification badge when there are new updates
 * Opens a changelog modal when clicked
 */
export const UpdatesNotification: React.FC<UpdatesNotificationProps> = ({ className = '' }) => {
  const [showBadge, setShowBadge] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Check if there are new updates on mount
    setShowBadge(hasNewUpdates());
  }, []);

  const handleClick = () => {
    setIsModalOpen(true);
    setShowBadge(false);
    // Mark current version as seen
    setLastSeenVersion(getCurrentVersion());
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Notification Button */}
      <button
        onClick={handleClick}
        className={`relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group ${className}`}
        aria-label="View updates"
        title="View updates"
      >
        <NotificationsIcon sx={{ fontSize: 20 }} className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100" />
        
        {/* Notification Badge */}
        {showBadge && (
          <span className="absolute top-1 right-1 flex h-3 w-3">
            {/* Animated ping effect */}
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            {/* Static badge */}
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
        )}
      </button>

      {/* Changelog Modal */}
      <ChangelogModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  );
};

